import { Button } from '@client/components/ui/button';
import { cn } from '@client/lib';
import { setSelectedAccountTypes } from '@client/model/ltcFinancialPlanningModel';
import { selectLtcFinancialPlanningSelectedAccountTypes } from '@client/model/selectors';
import { ChevronDown, ChevronUp, Plus } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ASSET_OPTIONS } from './AssetSelectionGrid';

interface AddAssetSectionProps {
  className?: string;
}

export function AddAssetSection({ className }: AddAssetSectionProps) {
  const dispatch = useDispatch();
  const selectedAccountTypes = useSelector(selectLtcFinancialPlanningSelectedAccountTypes);
  const [isExpanded, setIsExpanded] = useState(false);
  const [selectedAssetType, setSelectedAssetType] = useState<LtcFinancialPlanningAssetType | null>(
    null,
  );

  const availableAssets = useMemo(() => {
    return ASSET_OPTIONS.filter(asset => !selectedAccountTypes.includes(asset.type));
  }, [selectedAccountTypes]);

  const handleToggle = () => {
    setIsExpanded(!isExpanded);
  };

  const handleAssetTypeSelect = (assetType: LtcFinancialPlanningAssetType) => {
    setSelectedAssetType(assetType);
  };

  const handleAddAsset = () => {
    if (!selectedAssetType) return;

    if (!selectedAccountTypes.includes(selectedAssetType)) {
      dispatch(setSelectedAccountTypes([...selectedAccountTypes, selectedAssetType]));
    }

    setSelectedAssetType(null);
    setIsExpanded(false);
  };

  return (
    <div
      className={cn(
        'overflow-hidden rounded-xl border border-dashed border-gray-300 bg-gray-50/50 text-left',
        className,
      )}
    >
      {/* Collapsed Header */}
      <button
        type="button"
        onClick={handleToggle}
        className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left transition-colors hover:bg-gray-100/50"
      >
        <div className="flex translate-y-0.5 items-start gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-gray-100 text-gray-500">
            <Plus className="h-5 w-5" />
          </div>
          <div>
            <span className="font-semibold text-gray-900">Add another funding source</span>
            <p className="text-sm text-gray-500">
              Need to include an asset you didn't add earlier?
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          {isExpanded ? (
            <ChevronUp className="h-5 w-5 text-gray-400" />
          ) : (
            <ChevronDown className="h-5 w-5 text-gray-400" />
          )}
        </div>
      </button>

      {/* Expanded Content */}
      {isExpanded && (
        <div className="border-t border-gray-200 bg-white px-5 py-4">
          {/* Asset Type Selection */}
          <div className="mb-6">
            <label className="mb-2 block text-left text-sm font-medium text-gray-700">
              Select Asset Type
            </label>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
              {availableAssets.map(asset => (
                <button
                  key={asset.type}
                  type="button"
                  onClick={() => handleAssetTypeSelect(asset.type)}
                  className={cn(
                    'rounded-lg border-2 px-3 py-2.5 text-left text-sm font-medium transition-all',
                    {
                      'border-primary bg-primary/5 text-gray-900': selectedAssetType === asset.type,
                      'border-gray-200 bg-white text-gray-700 hover:border-gray-300':
                        selectedAssetType !== asset.type,
                    },
                  )}
                >
                  {asset.label}
                </button>
              ))}
            </div>
          </div>

          {/* Add Button */}
          {selectedAssetType && (
            <Button
              onClick={handleAddAsset}
              className="w-full"
            >
              Add {ASSET_OPTIONS.find(a => a.type === selectedAssetType)?.label}
            </Button>
          )}

          {/* Help Text */}
          {!selectedAssetType && (
            <p className="text-center text-sm text-gray-500">
              Select an asset type above to add it as a funding source
            </p>
          )}
        </div>
      )}
    </div>
  );
}
